import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, { buildFaqSchema, buildLocalBusinessSchema } from "@/components/SeoLandingPage";
import { buildBreadcrumbSchema, ldScript, pageHead, SITE_URL } from "@/lib/page-schema";
import heroImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-party-bus-interior-bar-tvs.jpg?format=webp&quality=72&w=1400&imagetools";
import stadiumImg from "@/assets/mercedes-benz-stadium-sprinter-limo-party-bus.jpg?format=webp&quality=72&w=1400&imagetools";

const PATH = "/party-bus-atlanta";
const TITLE = "Sprinter Party Bus Atlanta | Chauffeured Party Bus Rental";
const DESC =
  "Our Limo Style Sprinter is Atlanta's chauffeured party bus experience: built-in bar, LED lighting and lounge seating for up to 14. Birthdays, bachelorettes, game days, concerts and holiday parties.";

const faqs = [
  {
    q: "How many people fit in your party bus?",
    a: "The Limo Style Sprinter seats up to 14 in lounge seating.",
  },
  {
    q: "Is a chauffeur included?",
    a: "Yes. Every ride comes with a chauffeur. You'll know your vehicle and your chauffeur before pickup, and you can track your chauffeur live in our app.",
  },
  {
    q: "Will the party bus in the photos be the one that arrives?",
    a: "Yes. These are photos of our own Limo Style Sprinter. You'll know your vehicle and your chauffeur before pickup, and you can track your chauffeur live in our app.",
  },
];

export const Route = createFileRoute("/party-bus-atlanta")({
  head: () =>
    pageHead({
      path: PATH,
      title: TITLE,
      description: DESC,
      scripts: [
        ldScript(
          buildLocalBusinessSchema({
            url: `${SITE_URL}${PATH}`,
            name: "Atlanta Luxury Sprinters, Sprinter Party Bus Atlanta",
            image: `${SITE_URL}/favicon.ico`,
            description: DESC,
            serviceType: "Party Bus Rental",
            areaServed: [{ type: "City", name: "Atlanta" }, { type: "City", name: "Buckhead" }],
          }),
        ),
        ldScript(buildFaqSchema(faqs)),
        ldScript(
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Party Bus Atlanta", path: PATH },
          ]),
        ),
      ],
    }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Party Bus • Atlanta"
      h1="Sprinter Party Bus in Atlanta"
      intro="You've planned a night together. Our party bus experience is the Mercedes-Benz Sprinter Limousine: lounge seating for up to 14, LED lighting, a built-in bar and your own professional chauffeur."
      hideQuotePricing
      heroImage={heroImage}
      heroAlt="Limo Style Sprinter party bus lounge with bar and LED lighting in Atlanta"
      midImage={midImage}
      midAlt="Sprinter party bus interior with bar and TVs"
      sectionOneTitle="Enjoy the Night Together"
      sectionOneBody={[
        "A party bus should feel like the best room of the night. Our Limo Style Sprinter is custom-built, 2025 or newer, and detailed before and after every job, so your group steps into a cabin that is ready for you.",
        "We own every vehicle in our fleet. You'll know your vehicle and your chauffeur before pickup, and you can track your chauffeur live in our app.",
      ]}
      bulletsTitle="What's On Board"
      bulletsIntro="Your Limo Style Sprinter, ready for the occasion."
      bullets={["Built-in bar", "LED lighting", "Lounge seating for up to 14", "A background-checked, fingerprinted chauffeur", "Detailed before and after every job"]}
      coverageTitle="Occasions We Book"
      coverageIntro="The nights our party bus is made for."
      coverage={["Birthdays", "Bachelorette parties", "Game days", "Concerts", "Holiday parties"]}
      gallery={{
        title: "Inside the Party Bus",
        images: [
          { src: heroImage, alt: "Limo Style Sprinter lounge and bar", caption: "Lounge and bar" },
          { src: midImage, alt: "Party bus bar and TVs", caption: "Bar and TVs" },
          { src: stadiumImg, alt: "Sprinter party bus at Mercedes-Benz Stadium", caption: "Game day at Mercedes-Benz Stadium" },
        ],
      }}
      faqs={faqs}
      related={[
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "The full Sprinter Limousine fleet." },
        { to: "/concert-sports-transportation-atlanta", title: "Concert & Sports Transportation", desc: "Stadium and arena nights." },
        { to: "/uga-game-day-transportation", title: "UGA Game Day", desc: "Atlanta to Athens on game day." },
      ]}
      ctaTitle="Text Me About Your Celebration"
      ctaBody="Send me your date, pickup, group size and stops. I'll check availability personally and send your custom quote in writing."
    />
  );
}
